import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
    Users,
    Search,
    RefreshCw,
    Loader2,
    ChevronDown,
    ChevronUp,
    Mail,
    AlertCircle,
    CheckCircle2,
    Circle,
    ExternalLink,
    Copy,
    Check,
    Trash2,
    RotateCcw,
    Globe,
    Activity,
    Tag,
    X,
    FileText,
    Phone,
    Clock,
    MapPin,
    Wrench,
    Megaphone,
    Image as ImageIcon,
    Star,
    Share2,
    Download,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { AdminSmsAddon } from "../components/AdminSmsAddon";

// ---------------------------------------------------------------------------
// Constants — kept in sync with backend INTAKE_BUILD_STATUSES + CHECKLIST_KEYS
// ---------------------------------------------------------------------------
const STAGES = [
    { value: "new_signup", label: "New signup", dot: "bg-zinc-400" },
    { value: "waiting_on_client", label: "Waiting on client", dot: "bg-amber-400" },
    { value: "intake_complete", label: "Intake complete", dot: "bg-sky-400" },
    { value: "in_build", label: "In build", dot: "bg-violet-400" },
    { value: "launched", label: "Launched", dot: "bg-emerald-400" },
];

const STAGE_BY_VALUE = Object.fromEntries(STAGES.map((s) => [s.value, s]));

const CHECKLIST = [
    { key: "welcome_email_sent", label: "Welcome email sent", auto: "welcome_email_ok" },
    { key: "onboarding_form_complete", label: "Onboarding form complete", auto: "onboarding_full" },
    { key: "business_info_reviewed", label: "Business info reviewed" },
    { key: "photos_assets_received", label: "Photos / assets received", auto: "photos_present" },
    { key: "website_draft_started", label: "Website draft started" },
    { key: "first_draft_sent", label: "First draft sent" },
    { key: "revisions_complete", label: "Revisions complete" },
    { key: "site_launched", label: "Site launched" },
];

const TAGS = [
    { value: "VIP", color: "bg-amber-500/10 border-amber-700/40 text-amber-200" },
    { value: "High priority", color: "bg-red-500/10 border-red-800/40 text-red-200" },
    { value: "Demo", color: "bg-sky-500/10 border-sky-800/40 text-sky-200" },
    { value: "Test", color: "bg-zinc-500/10 border-zinc-700/40 text-zinc-300" },
];

const TAG_COLOR = Object.fromEntries(TAGS.map((t) => [t.value, t.color]));

// "Needs attention" heuristic — used by the filter pill
const needsAttention = (c) => {
    if (c.build_status === "launched") return false;
    const old = c.created_at && Date.now() - new Date(c.created_at).getTime() > 24 * 3600 * 1000;
    return (
        c.welcome_email_ok === false
        || (old && c.onboarding_percent < 60)
        || c.welcome_email_skipped === true
        || !!c.welcome_email_error
    );
};

const fmtSince = (iso) => {
    if (!iso) return "—";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.round(days / 30);
    return `${months}mo ago`;
};

const copyToClipboard = async (value, label = "Copied") => {
    try {
        await navigator.clipboard.writeText(value);
        toast(label, { duration: 1500 });
    } catch (_e) {
        toast.error("Copy failed");
    }
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AdminClients() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | needs_attention | trash | <stage> | <tag>
    const [search, setSearch] = useState("");
    const [openId, setOpenId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null); // client row pending delete

    const load = useCallback(async (params = {}) => {
        try {
            const q = params.trash ? "?include_deleted=true" : "";
            const { data } = await api.get(`/admin/clients${q}`);
            setClients(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load clients");
            setClients([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        load({ trash: filter === "trash" });
    }, [filter, load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return clients.filter((c) => {
            if (q) {
                const hay = `${c.business_name} ${c.owner_email} ${c.plan_name} ${(c.tags || []).join(" ")}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (filter === "all" || filter === "trash") return true;
            if (filter === "needs_attention") return needsAttention(c);
            if (filter.startsWith("tag:")) {
                const tag = filter.slice(4);
                return (c.tags || []).includes(tag);
            }
            return c.build_status === filter;
        });
    }, [clients, filter, search]);

    const counts = useMemo(() => {
        const out = { all: clients.length, needs_attention: 0 };
        STAGES.forEach((s) => { out[s.value] = 0; });
        TAGS.forEach((t) => { out[`tag:${t.value}`] = 0; });
        clients.forEach((c) => {
            out[c.build_status] = (out[c.build_status] || 0) + 1;
            if (needsAttention(c)) out.needs_attention += 1;
            (c.tags || []).forEach((tag) => {
                out[`tag:${tag}`] = (out[`tag:${tag}`] || 0) + 1;
            });
        });
        return out;
    }, [clients]);

    const updateLocal = (tenant_id, patch) => {
        setClients((prev) => prev.map((c) => (c.tenant_id === tenant_id ? { ...c, ...patch } : c)));
    };

    const onConfirmDelete = async (client) => {
        try {
            await api.delete(`/admin/clients/${client.tenant_id}`);
            toast.success(`Moved ${client.business_name} to Trash`);
            setConfirmDelete(null);
            setOpenId(null);
            load({ trash: filter === "trash" });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
        }
    };

    const onRestore = async (client) => {
        try {
            await api.post(`/admin/clients/${client.tenant_id}/restore`);
            toast.success(`Restored ${client.business_name}`);
            load({ trash: filter === "trash" });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to restore");
        }
    };

    const inTrash = filter === "trash";

    return (
        <section className="mx-auto w-full max-w-5xl px-5 sm:px-8 pt-6 sm:pt-16 pb-40 md:pb-16" data-testid="admin-clients-page">
            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-950/60 text-[11.5px] tracking-tight text-zinc-400"
            >
                <Users className="h-3 w-3" />
                WebJesters Intake
            </motion.div>

            <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="mt-6 text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-50"
            >
                {inTrash ? "Trash" : "Clients"}
            </motion.h1>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-zinc-400">
                {inTrash
                    ? "Soft-deleted clients. Restore brings them back exactly as they were."
                    : "Everyone who's signed up, what they bought, and what step they're at in the build."}
            </p>

            {/* Filter row */}
            <div className="mt-8 sm:mt-10 flex items-center gap-2 flex-wrap">
                <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testid="clients-filter-all">
                    All <span className="ml-1.5 text-zinc-500">{counts.all}</span>
                </FilterPill>
                <FilterPill
                    active={filter === "needs_attention"}
                    onClick={() => setFilter("needs_attention")}
                    testid="clients-filter-needs-attention"
                    tone="amber"
                >
                    Needs attention <span className="ml-1.5 text-amber-400/80">{counts.needs_attention}</span>
                </FilterPill>
                {STAGES.map((s) => (
                    <FilterPill
                        key={s.value}
                        active={filter === s.value}
                        onClick={() => setFilter(s.value)}
                        testid={`clients-filter-${s.value}`}
                    >
                        <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 ${s.dot}`} />
                        {s.label} <span className="ml-1.5 text-zinc-500">{counts[s.value] || 0}</span>
                    </FilterPill>
                ))}
                {TAGS.filter((t) => counts[`tag:${t.value}`] > 0).map((t) => (
                    <FilterPill
                        key={t.value}
                        active={filter === `tag:${t.value}`}
                        onClick={() => setFilter(`tag:${t.value}`)}
                        testid={`clients-filter-tag-${t.value.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <Tag className="h-3 w-3 mr-1" /> {t.value}{" "}
                        <span className="ml-1.5 text-zinc-500">{counts[`tag:${t.value}`]}</span>
                    </FilterPill>
                ))}
                <FilterPill
                    active={filter === "trash"}
                    onClick={() => setFilter("trash")}
                    testid="clients-filter-trash"
                >
                    <Trash2 className="h-3 w-3 mr-1" /> Trash
                </FilterPill>
            </div>

            {/* Search + refresh */}
            <div className="mt-4 flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-600" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by business, email, package, or tag"
                        data-testid="clients-search-input"
                        className="w-full h-10 rounded-md bg-transparent border border-zinc-800 pl-9 pr-3 text-[13.5px] text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-500 transition-colors"
                    />
                </div>
                <button
                    type="button"
                    onClick={() => load({ trash: inTrash })}
                    aria-label="Refresh clients"
                    data-testid="clients-refresh-button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* Empty / loading states */}
            {loading && (
                <p className="mt-10 text-[13.5px] text-zinc-500" data-testid="clients-loading">
                    Loading clients…
                </p>
            )}
            {!loading && filtered.length === 0 && (
                <p className="mt-10 text-[13.5px] text-zinc-500" data-testid="clients-empty">
                    {inTrash ? "Trash is empty." : "No clients match this filter yet."}
                </p>
            )}

            {/* List */}
            <ul className="mt-6 space-y-3" data-testid="clients-list">
                {filtered.map((c) => (
                    <ClientRow
                        key={c.tenant_id}
                        client={c}
                        open={openId === c.tenant_id}
                        trashMode={inTrash}
                        onToggle={() =>
                            setOpenId((prev) => (prev === c.tenant_id ? null : c.tenant_id))
                        }
                        onUpdated={(patch) => updateLocal(c.tenant_id, patch)}
                        onDelete={() => setConfirmDelete(c)}
                        onRestore={() => onRestore(c)}
                    />
                ))}
            </ul>

            <DeleteConfirmDialog
                client={confirmDelete}
                onCancel={() => setConfirmDelete(null)}
                onConfirm={onConfirmDelete}
            />

            <Toaster
                theme="dark"
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid #27272a",
                        color: "#f4f4f5",
                    },
                }}
            />
        </section>
    );
}

// ---------------------------------------------------------------------------
// FilterPill
// ---------------------------------------------------------------------------
function FilterPill({ active, onClick, children, testid, tone }) {
    const activeCls = tone === "amber"
        ? "bg-amber-950/30 border-amber-800/60 text-amber-200"
        : "bg-zinc-50 border-zinc-50 text-zinc-950";
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={[
                "h-8 px-3 rounded-full border text-[12.5px] inline-flex items-center transition-colors whitespace-nowrap",
                active ? activeCls : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

// ---------------------------------------------------------------------------
// ClientRow
// ---------------------------------------------------------------------------
function ClientRow({ client, open, trashMode, onToggle, onUpdated, onDelete, onRestore }) {
    const [savingStage, setSavingStage] = useState(false);

    const stage = STAGE_BY_VALUE[client.build_status] || STAGES[0];
    const attention = needsAttention(client);

    const onStageChange = async (value) => {
        setSavingStage(true);
        try {
            await api.patch(`/admin/clients/${client.tenant_id}/intake`, { build_status: value });
            onUpdated({ build_status: value, build_status_auto: false });
            toast.success(`Stage → ${STAGE_BY_VALUE[value].label}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update stage");
        } finally {
            setSavingStage(false);
        }
    };

    return (
        <li
            data-testid={`client-card-${client.tenant_id}`}
            className={[
                "rounded-xl border bg-zinc-950/40 overflow-hidden",
                trashMode ? "border-zinc-800/60 opacity-80" : (attention ? "border-amber-900/40" : "border-zinc-800/80"),
            ].join(" ")}
        >
            <button
                type="button"
                onClick={onToggle}
                data-testid={`client-toggle-${client.tenant_id}`}
                className="w-full text-left px-4 sm:px-5 py-4 hover:bg-zinc-950/60 transition-colors"
            >
                <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p
                                className="text-[14.5px] font-medium text-zinc-50 truncate"
                                data-testid={`client-business-${client.tenant_id}`}
                            >
                                {client.business_name || "—"}
                            </p>
                            <LoginStatePill client={client} />
                            {(client.tags || []).map((t) => (
                                <span
                                    key={t}
                                    className={`inline-flex items-center h-5 px-1.5 rounded border text-[10.5px] ${TAG_COLOR[t] || "bg-zinc-800/60 border-zinc-700 text-zinc-300"}`}
                                    data-testid={`client-tag-${client.tenant_id}-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                        <p className="mt-0.5 text-[12.5px] text-zinc-400 truncate">
                            {client.owner_email}
                            <span className="text-zinc-700 mx-1.5">·</span>
                            <span className="text-zinc-500">{fmtSince(client.created_at)}</span>
                            {client.last_activity_at && (
                                <>
                                    <span className="text-zinc-700 mx-1.5">·</span>
                                    <span className="text-zinc-500" title={`Last activity ${client.last_activity_at}`}>
                                        active {fmtSince(client.last_activity_at)}
                                    </span>
                                </>
                            )}
                        </p>
                        <p className="mt-1 text-[12px] text-zinc-500 truncate">
                            {client.plan_name ? `📦 ${client.plan_name}` : (
                                <span className="text-zinc-600">No package recorded</span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <StagePill stage={stage} auto={client.build_status_auto} />
                        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <WelcomeBadge client={client} />
                    <OnboardingBadge percent={client.onboarding_percent} missing={client.onboarding_missing} />
                    <PhotosBadge count={client.photo_count} />
                </div>
            </button>

            {open && (
                <div
                    className="border-t border-zinc-800/80 px-4 sm:px-5 py-4 space-y-5"
                    data-testid={`client-detail-${client.tenant_id}`}
                >
                    {trashMode ? (
                        <div className="space-y-3">
                            <p className="text-[13px] text-zinc-400">
                                Soft-deleted{client.deleted_at ? ` ${fmtSince(client.deleted_at)}` : ""}. Tenant data is retained.
                            </p>
                            <button
                                type="button"
                                onClick={onRestore}
                                data-testid={`client-restore-${client.tenant_id}`}
                                className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-zinc-50 text-zinc-950 text-[13px] font-medium hover:bg-white transition-colors"
                            >
                                <RotateCcw className="h-4 w-4" />
                                Restore client
                            </button>
                        </div>
                    ) : (
                        <>
                            <QuickActions client={client} onUpdated={onUpdated} onDelete={onDelete} />
                            <StageSelector client={client} savingStage={savingStage} onChange={onStageChange} />
                            <Checklist client={client} onUpdated={onUpdated} />
                            <SubmittedInfo tenantId={client.tenant_id} />
                            <TagPicker client={client} onUpdated={onUpdated} />
                            <AdminSmsAddon key={client.tenant_id} tenantId={client.tenant_id} />
                            <WebsiteUrl client={client} onUpdated={onUpdated} />
                            <NotesEditor client={client} onUpdated={onUpdated} />
                            <ActivityTimeline tenantId={client.tenant_id} key={client.intake_updated_at} />
                        </>
                    )}
                </div>
            )}
        </li>
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function StagePill({ stage, auto }) {
    return (
        <span
            className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full border border-zinc-800 bg-zinc-950 text-[11px] text-zinc-300"
            title={auto ? "Auto-set by WebJesters — admin can override" : undefined}
        >
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${stage.dot}`} />
            {stage.label}
            {auto && <span className="text-zinc-500 text-[10px]">· auto</span>}
        </span>
    );
}

function WelcomeBadge({ client }) {
    if (client.welcome_email_ok === true) {
        return <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-300/90" data-testid="badge-welcome-sent"><Mail className="h-3 w-3" />Welcome sent</span>;
    }
    if (client.welcome_email_skipped) {
        return <span className="inline-flex items-center gap-1 text-[11.5px] text-amber-300/90" title="RESEND_API_KEY / RESEND_FROM_EMAIL missing" data-testid="badge-welcome-skipped"><AlertCircle className="h-3 w-3" />Welcome skipped</span>;
    }
    if (client.welcome_email_error) {
        return <span className="inline-flex items-center gap-1 text-[11.5px] text-red-300/90 truncate max-w-[260px]" title={client.welcome_email_error} data-testid="badge-welcome-failed"><AlertCircle className="h-3 w-3 shrink-0" />Welcome failed</span>;
    }
    return <span className="inline-flex items-center gap-1 text-[11.5px] text-zinc-500" data-testid="badge-welcome-unknown"><Mail className="h-3 w-3" />Welcome status unknown</span>;
}

function OnboardingBadge({ percent, missing }) {
    const tone = percent >= 80 ? "text-emerald-300/90" : percent >= 40 ? "text-amber-300/90" : "text-zinc-500";
    const title = missing && missing.length > 0 ? `Missing: ${missing.join(", ")}` : "All fields filled";
    return <span className={`inline-flex items-center gap-1 text-[11.5px] ${tone}`} title={title} data-testid="badge-onboarding">Onboarding {percent}%</span>;
}

function PhotosBadge({ count }) {
    if (!count) {
        return <span className="inline-flex items-center gap-1 text-[11.5px] text-zinc-500" data-testid="badge-photos-empty">No photos yet</span>;
    }
    return <span className="inline-flex items-center gap-1 text-[11.5px] text-zinc-400" data-testid="badge-photos">{count} photo{count === 1 ? "" : "s"}</span>;
}

/**
 * Compact pill that surfaces customer-login health right next to the business
 * name on the Clients list. Three states:
 *   • Never logged in   → amber "Awaiting first login" (most important — flags
 *                          paying customers who can't get in for any reason)
 *   • Logged in <30d    → silent (no pill — keeps the card clean)
 *   • Logged in >30d    → muted "Inactive 60d" hint
 *
 * Uses only the new `last_login_at` + `login_count` fields surfaced on the
 * /api/admin/clients payload — zero extra calls.
 */
function LoginStatePill({ client }) {
    const lastLogin = client.last_login_at;
    const count = client.login_count || 0;
    // Never logged in — only meaningful once the welcome email has shipped
    // (otherwise we'd flag tenants mid-provisioning).
    if (!lastLogin || count === 0) {
        const welcomed = client.welcome_email_ok || client.welcome_email_skipped;
        if (!welcomed) return null;
        return (
            <span
                data-testid={`login-pill-awaiting-${client.tenant_id}`}
                title="Customer hasn't logged in since signup. Click 'Send setup link' if stuck."
                className="inline-flex items-center gap-1 h-5 px-2 rounded-full border border-amber-700/40 bg-amber-500/[0.07] text-[10.5px] text-amber-300 uppercase tracking-wider"
            >
                <AlertCircle className="h-3 w-3" />
                Awaiting first login
            </span>
        );
    }
    // Long-stale check
    const daysSince = Math.floor((Date.now() - new Date(lastLogin).getTime()) / 86_400_000);
    if (daysSince >= 30) {
        return (
            <span
                data-testid={`login-pill-stale-${client.tenant_id}`}
                title={`Last login ${daysSince} days ago (${lastLogin})`}
                className="inline-flex items-center gap-1 h-5 px-2 rounded-full border border-zinc-800 bg-zinc-950/60 text-[10.5px] text-zinc-500 uppercase tracking-wider"
            >
                Inactive {daysSince}d
            </span>
        );
    }
    return null;
}

function QuickActions({ client, onDelete }) {
    const [creatingLink, setCreatingLink] = useState(false);

    const copyTenantId = () => copyToClipboard(client.tenant_id, "Tenant ID copied");
    const copyWebhookSecret = async () => {
        try {
            const { data } = await api.get(`/admin/tenants/${client.tenant_id}/webhook`);
            await navigator.clipboard.writeText(data.secret);
            toast("Webhook secret copied · keep it private");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to fetch secret");
        }
    };
    const copyLoginLink = async () => {
        setCreatingLink(true);
        try {
            const { data } = await api.post(`/admin/clients/${client.tenant_id}/login-link`);
            await navigator.clipboard.writeText(data.url);
            toast.success("One-time login link copied · expires in 4 hours");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate link");
        } finally {
            setCreatingLink(false);
        }
    };

    return (
        <div>
            <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">Quick actions</p>
            <div className="flex flex-wrap gap-2">
                <QuickButton onClick={copyTenantId} testid={`client-copy-id-${client.tenant_id}`} icon={Copy} label="Tenant ID" />
                <QuickButton onClick={copyWebhookSecret} testid={`client-copy-webhook-${client.tenant_id}`} icon={Copy} label="Webhook secret" />
                <QuickButton onClick={copyLoginLink} testid={`client-copy-login-${client.tenant_id}`} icon={creatingLink ? Loader2 : Copy} label="One-time login link" spinning={creatingLink} />
                {client.website_url && (
                    <a
                        href={client.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`client-open-website-${client.tenant_id}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-800 text-[12.5px] text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                    >
                        <Globe className="h-3.5 w-3.5" />
                        Open live site
                        <ExternalLink className="h-3 w-3" />
                    </a>
                )}
                <button
                    type="button"
                    onClick={onDelete}
                    data-testid={`client-delete-${client.tenant_id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-red-900/40 text-[12.5px] text-red-300 hover:bg-red-950/30 hover:border-red-800/60 transition-colors ml-auto"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete client
                </button>
            </div>
        </div>
    );
}

function QuickButton({ onClick, testid, icon: Icon, label, spinning }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            disabled={spinning}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-800 text-[12.5px] text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 transition-colors disabled:opacity-60"
        >
            <Icon className={`h-3.5 w-3.5 ${spinning ? "animate-spin" : ""}`} />
            {label}
        </button>
    );
}

function StageSelector({ client, savingStage, onChange }) {
    return (
        <div>
            <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">Build stage</p>
            <div className="flex items-center gap-2 flex-wrap">
                {STAGES.map((s) => (
                    <button
                        key={s.value}
                        type="button"
                        onClick={() => onChange(s.value)}
                        disabled={savingStage || s.value === client.build_status}
                        data-testid={`client-stage-${client.tenant_id}-${s.value}`}
                        className={[
                            "h-8 px-3 rounded-md border text-[12px] inline-flex items-center transition-colors",
                            s.value === client.build_status
                                ? "bg-zinc-50 text-zinc-950 border-zinc-50"
                                : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700",
                            savingStage ? "opacity-60 cursor-wait" : "",
                        ].join(" ")}
                    >
                        <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 ${s.dot}`} />
                        {s.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function Checklist({ client, onUpdated }) {
    const [saving, setSaving] = useState(null);
    const autoSignals = {
        welcome_email_ok: client.welcome_email_ok === true,
        onboarding_full: client.onboarding_percent >= 80,
        photos_present: (client.photo_count || 0) > 0,
    };

    const onToggle = async (key, next) => {
        setSaving(key);
        const nextChecklist = { ...client.checklist, [key]: next };
        try {
            await api.patch(`/admin/clients/${client.tenant_id}/intake`, { checklist: { [key]: next } });
            onUpdated({ checklist: nextChecklist });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
        } finally {
            setSaving(null);
        }
    };

    return (
        <div>
            <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">Build checklist</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                {CHECKLIST.map((item) => {
                    const checked = !!client.checklist?.[item.key];
                    const auto = item.auto ? autoSignals[item.auto] : null;
                    const isSaving = saving === item.key;
                    return (
                        <li key={item.key} className="flex items-start gap-2 py-1">
                            <button
                                type="button"
                                onClick={() => onToggle(item.key, !checked)}
                                disabled={isSaving}
                                data-testid={`checklist-${client.tenant_id}-${item.key}`}
                                aria-pressed={checked}
                                className="mt-0.5 shrink-0"
                            >
                                {isSaving ? <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                                    : checked ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                                        : <Circle className="h-4 w-4 text-zinc-600 hover:text-zinc-400 transition-colors" />}
                            </button>
                            <div className="min-w-0 flex-1">
                                <p className={`text-[13px] ${checked ? "text-zinc-200" : "text-zinc-400"}`}>{item.label}</p>
                                {auto === true && !checked && (
                                    <p className="text-[10.5px] text-emerald-400/70" data-testid={`checklist-auto-${item.key}`}>Auto-detected · tap to confirm</p>
                                )}
                                {auto === false && checked && (
                                    <p className="text-[10.5px] text-amber-400/70">Manual override — system signal says not yet</p>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function TagPicker({ client, onUpdated }) {
    const [saving, setSaving] = useState(false);
    const current = client.tags || [];

    const toggle = async (tag) => {
        const next = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
        setSaving(true);
        try {
            await api.patch(`/admin/tenants/${client.tenant_id}`, { tags: next });
            onUpdated({ tags: next });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update tags");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">Tags</p>
            <div className="flex flex-wrap gap-2">
                {TAGS.map((t) => {
                    const on = current.includes(t.value);
                    return (
                        <button
                            key={t.value}
                            type="button"
                            onClick={() => toggle(t.value)}
                            disabled={saving}
                            data-testid={`client-tagpicker-${client.tenant_id}-${t.value.toLowerCase().replace(/\s+/g, "-")}`}
                            className={[
                                "inline-flex items-center gap-1 h-7 px-2.5 rounded-md border text-[11.5px] transition-colors",
                                on ? t.color : "border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700",
                            ].join(" ")}
                        >
                            {on && <Check className="h-3 w-3" />}
                            {t.value}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function WebsiteUrl({ client, onUpdated }) {
    const [value, setValue] = useState(client.website_url || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setValue(client.website_url || "");
    }, [client.tenant_id, client.website_url]);

    const save = async () => {
        const trimmed = value.trim();
        if (trimmed === (client.website_url || "")) return;
        setSaving(true);
        try {
            await api.patch(`/admin/tenants/${client.tenant_id}`, { website_url: trimmed });
            onUpdated({ website_url: trimmed });
            toast.success("Website URL saved");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Live website URL</p>
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />}
            </div>
            <input
                type="url"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={save}
                placeholder="https://acmeplumbing.com"
                data-testid={`client-website-${client.tenant_id}`}
                className="w-full h-10 rounded-md bg-transparent border border-zinc-800 px-3 text-[13px] text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-500 transition-colors"
            />
        </div>
    );
}

function NotesEditor({ client, onUpdated }) {
    const [value, setValue] = useState(client.notes || "");
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        setValue(client.notes || "");
    }, [client.tenant_id, client.notes]);

    const save = async () => {
        if (value === (client.notes || "")) return;
        setSaving(true);
        try {
            await api.patch(`/admin/clients/${client.tenant_id}/intake`, { notes: value });
            onUpdated({ notes: value });
            setSavedAt(Date.now());
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save notes");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Notes</p>
                <p className="text-[10.5px] text-zinc-600">{saving ? "Saving…" : savedAt ? "Saved" : "Auto-saves on blur"}</p>
            </div>
            <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={save}
                rows={3}
                maxLength={5000}
                placeholder="Anything you want to remember about this build…"
                data-testid={`client-notes-${client.tenant_id}`}
                className="w-full rounded-md bg-transparent border border-zinc-800 px-3 py-2.5 text-[13px] text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-500 transition-colors leading-relaxed"
            />
        </div>
    );
}

function ActivityTimeline({ tenantId }) {
    const [events, setEvents] = useState(null);
    const [loading, setLoading] = useState(false);
    const fetchedFor = useRef(null);

    useEffect(() => {
        if (fetchedFor.current === tenantId) return;
        fetchedFor.current = tenantId;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/admin/clients/${tenantId}/activity?limit=25`);
                setEvents(Array.isArray(data) ? data : []);
            } catch (_e) {
                setEvents([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [tenantId]);

    return (
        <div data-testid={`client-activity-${tenantId}`}>
            <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2 flex items-center gap-1.5">
                <Activity className="h-3 w-3" />
                Activity
            </p>
            {loading && <p className="text-[12.5px] text-zinc-500">Loading…</p>}
            {!loading && events && events.length === 0 && (
                <p className="text-[12.5px] text-zinc-500">No activity yet.</p>
            )}
            {!loading && events && events.length > 0 && (
                <ul className="space-y-2 max-h-60 overflow-y-auto pr-1">
                    {events.map((e, i) => (
                        <li key={i} className="flex items-start gap-2 text-[12.5px]">
                            <span className="inline-block mt-1.5 h-1.5 w-1.5 rounded-full bg-zinc-600 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <p className="text-zinc-300 leading-snug">{e.message || e.type}</p>
                                <p className="text-[10.5px] text-zinc-600 mt-0.5">
                                    {fmtSince(e.created_at)} · <span className="text-zinc-500">{e.actor || "system"}</span>
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// SubmittedInfo — read-only view of the client's business profile + photos
// ---------------------------------------------------------------------------
const DAY_LABELS = {
    mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

const formatTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":");
    const hh = parseInt(h, 10);
    if (isNaN(hh)) return t;
    const ampm = hh >= 12 ? "PM" : "AM";
    const display = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
    return `${display}:${m} ${ampm}`;
};

function DownloadPhotosButton({ tenantId, businessName }) {
    const [busy, setBusy] = useState(false);

    const onClick = async () => {
        setBusy(true);
        try {
            // Use the underlying axios instance so cookies/auth flow through,
            // but request a binary response so we can build a Blob URL.
            const res = await api.get(`/admin/clients/${tenantId}/photos.zip`, {
                responseType: "blob",
            });

            // Safety belt #1: force the blob's MIME type to application/zip
            // even if axios/Cloudflare's auto-decompression layer dropped the
            // upstream Content-Type. Some browsers (Safari, certain Chrome
            // builds on macOS) infer the download extension from blob.type,
            // not from the `download` attribute, when the blob was created
            // from an opaque/typeless source. Forcing the type guarantees a
            // real .zip on every OS.
            const blob = res.data instanceof Blob
                ? new Blob([res.data], { type: "application/zip" })
                : new Blob([res.data], { type: "application/zip" });

            // Safety belt #2: prefer the server-issued filename
            // (Content-Disposition) over the client-side slug. Falls back to
            // the slug if the header is missing.
            const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"] || "";
            const cdMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
            const slug = (businessName || "client")
                .toLowerCase()
                .replace(/[^a-z0-9-_]+/g, "-")
                .replace(/^-+|-+$/g, "") || "client";
            let filename = (cdMatch && cdMatch[1] && cdMatch[1].trim()) || `${slug}-photos.zip`;
            // Safety belt #3: guarantee the .zip extension no matter what.
            if (!/\.zip$/i.test(filename)) filename += ".zip";

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Defer revoke so Safari can finish writing the file before the
            // object URL is invalidated.
            setTimeout(() => window.URL.revokeObjectURL(url), 4000);
            toast.success("Photos zipped — check your downloads");
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Download failed";
            toast.error(typeof detail === "string" ? detail : "Download failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={busy}
            data-testid={`submitted-download-photos-${tenantId}`}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-zinc-800 text-[12px] text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 transition-colors disabled:opacity-60"
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {busy ? "Zipping…" : "Download all (.zip)"}
        </button>
    );
}

function SubmittedInfo({ tenantId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const fetchedFor = useRef(null);

    useEffect(() => {
        if (fetchedFor.current === tenantId) return;
        fetchedFor.current = tenantId;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/admin/clients/${tenantId}/profile`);
                setData(data);
            } catch (_e) {
                setData(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [tenantId]);

    if (loading) {
        return (
            <div data-testid={`client-submitted-${tenantId}`}>
                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2 flex items-center gap-1.5">
                    <FileText className="h-3 w-3" />
                    Submitted info
                </p>
                <p className="text-[12.5px] text-zinc-500">Loading…</p>
            </div>
        );
    }
    if (!data) {
        return (
            <div data-testid={`client-submitted-${tenantId}`}>
                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2 flex items-center gap-1.5">
                    <FileText className="h-3 w-3" />
                    Submitted info
                </p>
                <p className="text-[12.5px] text-zinc-500">Could not load.</p>
            </div>
        );
    }

    const { profile, media, completion } = data;
    const hours = Array.isArray(profile.hours) ? profile.hours : [];
    const serviceAreas = Array.isArray(profile.service_areas) ? profile.service_areas : [];
    const services = Array.isArray(profile.service_descriptions) ? profile.service_descriptions : [];
    const socialLinks = profile.social_links || {};
    const socialEntries = Object.entries(socialLinks).filter(([, v]) => (v || "").trim());
    const testimonials = Array.isArray(profile.testimonials) ? profile.testimonials : [];

    return (
        <div data-testid={`client-submitted-${tenantId}`} className="rounded-lg border border-zinc-800/80 bg-zinc-950/30 p-4 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-1.5">
                    <FileText className="h-3 w-3" />
                    Submitted info
                </p>
                <div className="flex items-center gap-2">
                    {completion.build_ready ? (
                        <span
                            data-testid="submitted-build-ready"
                            className="inline-flex items-center gap-1 px-2 h-5 rounded-full border border-emerald-700/40 bg-emerald-500/[0.08] text-[10.5px] text-emerald-300 uppercase tracking-wider"
                        >
                            <CheckCircle2 className="h-3 w-3" />
                            Build ready
                        </span>
                    ) : (
                        <span
                            data-testid="submitted-build-not-ready"
                            className="inline-flex items-center gap-1 px-2 h-5 rounded-full border border-amber-700/40 bg-amber-500/[0.06] text-[10.5px] text-amber-300/90 uppercase tracking-wider"
                        >
                            <AlertCircle className="h-3 w-3" />
                            Needs info
                        </span>
                    )}
                    <p className="text-[11px] text-zinc-500">{completion.percent}% complete</p>
                </div>
            </div>

            {/* Logo */}
            <div>
                <p className="text-[11px] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <ImageIcon className="h-3 w-3" /> Logo
                </p>
                {profile.logo_media_id ? (
                    <a
                        href={`${api.defaults.baseURL}/public/tenant/${tenantId}/logo`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="submitted-logo"
                        className="inline-block rounded-md border border-zinc-800 bg-zinc-950 p-2 hover:border-zinc-700 transition-colors"
                    >
                        <img
                            src={`${api.defaults.baseURL}/public/tenant/${tenantId}/logo`}
                            alt="Logo"
                            className="h-16 w-auto max-w-[140px] object-contain"
                        />
                    </a>
                ) : profile.logo_skipped ? (
                    <p className="text-[12.5px] text-zinc-400 italic" data-testid="submitted-logo-skipped">
                        Client opted out — design a logo as part of the build.
                    </p>
                ) : (
                    <p className="text-[12.5px] text-zinc-600 italic">Not provided yet</p>
                )}
            </div>

            {/* About */}
            <div>
                <p className="text-[11px] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <FileText className="h-3 w-3" /> About
                </p>
                {(profile.about || "").trim() ? (
                    <p data-testid="submitted-about" className="text-[13px] text-zinc-300 leading-relaxed whitespace-pre-line">
                        {profile.about}
                    </p>
                ) : (
                    <p className="text-[12.5px] text-zinc-600 italic">Not provided yet</p>
                )}
            </div>

            {/* Contact */}
            <Field icon={Phone} label="Phone" value={profile.phone} testid="submitted-phone" copyable />
            <Field icon={Mail} label="Email" value={profile.email} testid="submitted-email" copyable />

            {/* Hours */}
            <div>
                <p className="text-[11px] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <Clock className="h-3 w-3" /> Hours
                </p>
                {hours.length === 0 ? (
                    <p className="text-[12.5px] text-zinc-600 italic">Not provided yet</p>
                ) : (
                    <ul className="text-[12.5px] text-zinc-300 space-y-0.5" data-testid="submitted-hours">
                        {hours.map((h) => (
                            <li key={h.day} className="flex items-center gap-3">
                                <span className="w-9 text-zinc-500">{DAY_LABELS[h.day] || h.day}</span>
                                {h.closed ? (
                                    <span className="text-zinc-600">Closed</span>
                                ) : (
                                    <span>{formatTime(h.open)} – {formatTime(h.close)}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Service areas */}
            <div>
                <p className="text-[11px] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" /> Service areas
                </p>
                {serviceAreas.length === 0 ? (
                    <p className="text-[12.5px] text-zinc-600 italic">None yet</p>
                ) : (
                    <div className="flex flex-wrap gap-1.5" data-testid="submitted-areas">
                        {serviceAreas.map((a, i) => (
                            <span key={i} className="inline-flex items-center h-6 px-2 rounded border border-zinc-800 bg-zinc-950 text-[11.5px] text-zinc-300">{a}</span>
                        ))}
                    </div>
                )}
            </div>

            {/* Services */}
            <div>
                <p className="text-[11px] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <Wrench className="h-3 w-3" /> Services
                </p>
                {services.length === 0 ? (
                    <p className="text-[12.5px] text-zinc-600 italic">None yet</p>
                ) : (
                    <ul className="space-y-2" data-testid="submitted-services">
                        {services.map((s, i) => (
                            <li key={i} className="border-l-2 border-zinc-800 pl-3">
                                <p className="text-[13px] text-zinc-200 font-medium">{s.title || `Service ${i + 1}`}</p>
                                {s.description && (
                                    <p className="mt-0.5 text-[12.5px] text-zinc-400 leading-snug whitespace-pre-line">{s.description}</p>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* CTA + promo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field icon={Megaphone} label="CTA text" value={profile.cta_text} testid="submitted-cta" />
                <Field icon={Megaphone} label="Promo text" value={profile.promo_text} testid="submitted-promo" />
            </div>

            {/* Google Business Profile */}
            <div>
                <p className="text-[11px] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <Globe className="h-3 w-3" /> Google Business Profile
                </p>
                {(profile.google_business_url || "").trim() ? (
                    <a
                        href={profile.google_business_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="submitted-google-business"
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-300 hover:text-zinc-100 underline decoration-zinc-700 hover:decoration-zinc-400 break-all"
                    >
                        {profile.google_business_url}
                        <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                ) : (
                    <p className="text-[12.5px] text-zinc-600 italic">Not provided yet</p>
                )}
            </div>

            {/* Social links */}
            <div>
                <p className="text-[11px] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <Share2 className="h-3 w-3" /> Social media
                </p>
                {socialEntries.length === 0 ? (
                    <p className="text-[12.5px] text-zinc-600 italic">None yet</p>
                ) : (
                    <ul className="space-y-1" data-testid="submitted-social-links">
                        {socialEntries.map(([key, url]) => (
                            <li key={key} className="flex items-center gap-2 text-[12.5px]">
                                <span className="w-20 shrink-0 text-zinc-500 capitalize">{key}</span>
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-zinc-300 hover:text-zinc-100 underline decoration-zinc-700 hover:decoration-zinc-400 break-all"
                                >
                                    {url}
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Testimonials */}
            <div>
                <p className="text-[11px] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                    <Star className="h-3 w-3" /> Testimonials
                </p>
                {testimonials.length === 0 ? (
                    <p className="text-[12.5px] text-zinc-600 italic">None yet</p>
                ) : (
                    <ul className="space-y-2.5" data-testid="submitted-testimonials">
                        {testimonials.map((t, i) => (
                            <li key={i} className="border-l-2 border-zinc-800 pl-3">
                                <div className="flex items-center gap-2 mb-0.5">
                                    {t.name ? (
                                        <span className="text-[12.5px] text-zinc-300 font-medium">{t.name}</span>
                                    ) : (
                                        <span className="text-[12px] text-zinc-600 italic">Anonymous</span>
                                    )}
                                    {t.rating ? (
                                        <span className="inline-flex items-center gap-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Star
                                                    key={n}
                                                    className={[
                                                        "h-3 w-3",
                                                        n <= t.rating ? "fill-amber-400 text-amber-400" : "text-zinc-700",
                                                    ].join(" ")}
                                                />
                                            ))}
                                        </span>
                                    ) : null}
                                </div>
                                <p className="text-[12.5px] text-zinc-400 leading-snug whitespace-pre-line">{t.text}</p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Photos */}
            <div>
                <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                        <ImageIcon className="h-3 w-3" /> Photos
                        <span className="text-zinc-600">({media.length})</span>
                    </p>
                    {media.length > 0 && (
                        <DownloadPhotosButton tenantId={tenantId} businessName={data.tenant?.business_name} />
                    )}
                </div>
                {media.length === 0 ? (
                    <p className="text-[12.5px] text-zinc-600 italic">No photos uploaded</p>
                ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="submitted-photos">
                        {media.slice(0, 12).map((m) => (
                            <a
                                key={m.id}
                                href={m.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="aspect-square rounded-md overflow-hidden border border-zinc-800 bg-zinc-900 hover:border-zinc-600 transition-colors"
                                title={m.original_filename || ""}
                            >
                                <img src={m.url} alt={m.original_filename || ""} loading="lazy" className="h-full w-full object-cover" />
                            </a>
                        ))}
                        {media.length > 12 && (
                            <div className="aspect-square rounded-md border border-dashed border-zinc-800 bg-zinc-950/50 flex items-center justify-center text-[11.5px] text-zinc-500">
                                +{media.length - 12} more
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ icon: Icon, label, value, testid, copyable }) {
    const display = value && value.toString().trim() ? value : null;
    return (
        <div>
            <p className="text-[11px] text-zinc-500 mb-1 flex items-center gap-1.5">
                <Icon className="h-3 w-3" /> {label}
            </p>
            {display ? (
                <div className="flex items-center gap-2">
                    <p className="text-[13px] text-zinc-200 truncate" data-testid={testid}>{display}</p>
                    {copyable && (
                        <button
                            type="button"
                            onClick={() => copyToClipboard(display, `${label} copied`)}
                            aria-label={`Copy ${label}`}
                            className="shrink-0 h-6 w-6 inline-flex items-center justify-center rounded text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900 transition-colors"
                        >
                            <Copy className="h-3 w-3" />
                        </button>
                    )}
                </div>
            ) : (
                <p className="text-[12.5px] text-zinc-600 italic" data-testid={testid}>Not provided yet</p>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Delete confirmation dialog — 2-step (type DELETE)
// ---------------------------------------------------------------------------
function DeleteConfirmDialog({ client, onCancel, onConfirm }) {
    const [confirm, setConfirm] = useState("");

    useEffect(() => {
        if (!client) setConfirm("");
    }, [client]);

    const canDelete = confirm.trim() === "DELETE";

    return (
        <AnimatePresence>
            {client && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
                    onClick={onCancel}
                    data-testid="delete-confirm-overlay"
                >
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full sm:max-w-[460px] bg-[#0a0a0a] border border-zinc-800 rounded-t-2xl sm:rounded-xl p-6"
                        data-testid="delete-confirm-dialog"
                    >
                        <div className="flex items-start justify-between">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-[0.16em] text-red-300/80">Delete client</p>
                                <h2 className="mt-1 text-xl font-medium text-zinc-50 tracking-tight truncate">{client.business_name}</h2>
                                <p className="mt-0.5 text-[12.5px] text-zinc-500">{client.owner_email}</p>
                            </div>
                            <button type="button" onClick={onCancel} aria-label="Close" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="mt-5 space-y-3 text-[13px] text-zinc-300 leading-relaxed">
                            <p>Moves this client to <span className="text-zinc-100 font-medium">Trash</span>. Their data is preserved and restorable.</p>
                            <ul className="text-[12.5px] text-zinc-500 list-disc pl-5 space-y-1">
                                <li>Login is blocked immediately</li>
                                <li>Tenant is hidden from /admin/clients</li>
                                <li>Email is freed — same address can sign up again</li>
                                <li>You can restore from the Trash tab anytime</li>
                            </ul>
                        </div>
                        <label className="block mt-5">
                            <span className="block text-[12px] text-zinc-400 mb-2">Type <span className="text-zinc-200 font-medium">DELETE</span> to confirm</span>
                            <input
                                type="text"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                data-testid="delete-confirm-input"
                                autoFocus
                                className="w-full h-11 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 outline-none focus:border-red-700 transition-colors"
                            />
                        </label>
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" onClick={onCancel} data-testid="delete-confirm-cancel" className="h-10 px-4 rounded-md border border-zinc-800 text-[13px] text-zinc-300 hover:bg-zinc-900 transition-colors">Cancel</button>
                            <button
                                type="button"
                                disabled={!canDelete}
                                onClick={() => onConfirm(client)}
                                data-testid="delete-confirm-submit"
                                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-red-600 text-white text-[13.5px] font-medium hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Trash2 className="h-4 w-4" />
                                Move to Trash
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
